import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
  type AxiosError
} from 'axios'
import { authErrorWord, authFailureCode } from '@/utils/authFlow'

// API Error type for better error handling
export interface ApiErrorResponse {
  message: string
  code?: string
  status?: number
  errors?: Record<string, string[]>
}

// Helper function to get the current auth token
// This allows the interceptor to use the current token without circular dependencies
function getAuthToken(): string | null {
  // Try to get token from localStorage first (most reliable)
  const storedToken = localStorage.getItem('auth_token')
  if (storedToken) {
    return storedToken
  }

  // Try to get token from auth store if available
  try {
    const pinia = (globalThis as any).__PINIA__
    if (pinia) {
      const store = pinia._s.get('auth')
      if (store?.token) {
        return store.token
      }
    }
  } catch {
    // Ignore errors
  }

  return null
}

// Endpoints where we should NOT attempt an automatic token refresh on 401
const AUTH_ENDPOINTS = ['/auth/me', '/auth/refresh', '/auth/login', '/auth/logout']

// Create axios instance with base configuration
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'https://api.staging.profitjam.io',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true // Send cookies with all requests
})

// Separate axios instance for refresh calls — bypasses interceptors to avoid recursion
export const refreshClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'https://api.staging.profitjam.io',
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true
})

// Coalesced refresh state — multiple concurrent 401s share a single refresh call
let isRefreshing = false
let refreshPromise: Promise<boolean> | null = null

// Renew an expired sign-in once, however many requests notice it at the same time.
function doRefresh(): Promise<boolean> {
  if (!isRefreshing) {
    isRefreshing = true
    refreshPromise = refreshClient
      .post('/public/auth/refresh', {})
      .then(() => true)
      .catch(() => false)
      .finally(() => {
        isRefreshing = false
        refreshPromise = null
      })
  }
  return refreshPromise!
}

// Helper: clear local auth state and redirect to login
function clearAuthAndRedirect(errorCode?: string) {
  localStorage.removeItem('auth_token')
  localStorage.removeItem('id_token')
  localStorage.removeItem('auth_user')

  const currentPath = window.location.pathname + window.location.search
  if (!currentPath.startsWith('/auth')) {
    sessionStorage.setItem('auth_redirect', currentPath)
    window.location.href = errorCode ? `/auth?error=${authErrorWord(errorCode)}` : '/auth/login'
  }
}

// Request interceptor - add Authorization header
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get the current auth token
    const token = getAuthToken()

    // Add the Bearer token to all requests if available
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// Response interceptor - handle errors globally
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError<ApiErrorResponse>) => {
    const status = error.response?.status
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    switch (status) {
      case 401: {
        const requestUrl = originalRequest?.url || ''
        const isAuthEndpoint = AUTH_ENDPOINTS.some(ep => requestUrl.includes(ep))

        // If this is an auth endpoint or we already retried, give up
        if (isAuthEndpoint) break
        if (originalRequest?._retry) {
          clearAuthAndRedirect()
          break
        }

        // Attempt a coalesced token refresh, then retry the original request
        const refreshed = await doRefresh()
        if (refreshed) {
          originalRequest._retry = true
          return api(originalRequest)
        }

        // Refresh failed — force re-login
        clearAuthAndRedirect()
        break
      }

      case 403:
        if (['EMAIL_VERIFICATION_REQUIRED', 'ACCOUNT_LINK_REQUIRED'].includes(error.response?.data?.code || '')) {
          clearAuthAndRedirect(authFailureCode(error))
          break
        }
        // Forbidden - user doesn't have permission
        console.error('Access forbidden - insufficient permissions')
        break

      case 404:
        // Not found
        console.error('Resource not found')
        break

      case 422:
        // Validation error
        console.error('Validation error:', error.response?.data?.errors)
        break

      case 500:
      case 502:
      case 503:
      case 504:
        // Server errors
        console.error('Server error:', error.response?.data?.message || 'An unexpected error occurred')
        break

      default:
        // Network error or other issues
        if (!error.response) {
          console.error('Network error - please check your connection')
        }
    }

    return Promise.reject(error)
  }
)

// Helper function types
type RequestConfig = Omit<AxiosRequestConfig, 'url' | 'method' | 'data'>

/**
 * GET request helper
 */
export async function get<T = unknown>(
  url: string,
  config?: RequestConfig
): Promise<AxiosResponse<T>> {
  return api.get<T>(url, config)
}

/**
 * POST request helper
 */
export async function post<T = unknown, D = unknown>(
  url: string,
  data?: D,
  config?: RequestConfig
): Promise<AxiosResponse<T>> {
  return api.post<T>(url, data, config)
}

/**
 * PUT request helper
 */
export async function put<T = unknown, D = unknown>(
  url: string,
  data?: D,
  config?: RequestConfig
): Promise<AxiosResponse<T>> {
  return api.put<T>(url, data, config)
}

/**
 * PATCH request helper
 */
export async function patch<T = unknown, D = unknown>(
  url: string,
  data?: D,
  config?: RequestConfig
): Promise<AxiosResponse<T>> {
  return api.patch<T>(url, data, config)
}

/**
 * DELETE request helper
 */
export async function del<T = unknown>(
  url: string,
  config?: RequestConfig
): Promise<AxiosResponse<T>> {
  return api.delete<T>(url, config)
}

// Export the axios instance as default
export default api
